<template>
  <div class="stacking-snackbar-container">
    <TransitionGroup name="snackbar-stack" tag="div">
      <div
        v-for="snack in visibleSnacks"
        :key="snack.id"
        class="snackbar-item"
        :class="[`snackbar-${snack.type}`, { 'snackbar-dismissible': snack.dismissible }]"
      >
        <div class="snackbar-content">
          <i v-if="snack.icon" :class="['snackbar-icon', snack.icon]"></i>
          <div class="snackbar-message">{{ snack.message }}</div>
          <button v-if="snack.dismissible" class="snackbar-close" @click="removeSnack(snack.id)" aria-label="Close">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <div
          v-if="snack.showProgress && snack.timeout > 0"
          class="snackbar-progress"
          :style="{ animationDuration: `${snack.timeout}ms` }"
        ></div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
  import { useSnackbar } from '@composables/useSnackbar';
  import { computed } from 'vue';

  const { snacks, removeSnack } = useSnackbar();

  const visibleSnacks = computed(() => snacks.value);
</script>

<style scoped>
  .stacking-snackbar-container {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
  }

  .snackbar-item {
    pointer-events: auto;
    min-width: 300px;
    max-width: 500px;
    border-radius: 4px;
    box-shadow:
      0 3px 5px -1px rgba(0, 0, 0, 0.2),
      0 6px 10px 0 rgba(0, 0, 0, 0.14),
      0 1px 18px 0 rgba(0, 0, 0, 0.12);
    overflow: hidden;
    position: relative;
  }

  .snackbar-content {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    gap: 12px;
    color: white;
  }

  .snackbar-icon {
    font-size: 20px;
    flex-shrink: 0;
  }

  .snackbar-message {
    flex: 1;
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
  }

  .snackbar-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: opacity 0.2s;
    flex-shrink: 0;
  }

  .snackbar-close:hover {
    opacity: 1;
  }

  .snackbar-close i {
    font-size: 18px;
  }

  .snackbar-success {
    background-color: var(--bs-success, #28a745);
  }

  .snackbar-error {
    background-color: var(--bs-danger, #dc3545);
  }

  .snackbar-warning {
    background-color: var(--bs-warning, #ffc107);
  }

  .snackbar-info {
    background-color: var(--bs-info, #17a2b8);
  }

  .snackbar-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background-color: rgba(255, 255, 255, 0.7);
    animation: progress-shrink linear forwards;
  }

  @keyframes progress-shrink {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }

  .snackbar-stack-enter-active {
    transition: all 0.3s ease-out;
  }

  .snackbar-stack-leave-active {
    transition: all 0.3s ease-in;
  }

  .snackbar-stack-enter-from {
    opacity: 0;
    transform: translateX(100px);
  }

  .snackbar-stack-leave-to {
    opacity: 0;
    transform: translateX(100px);
  }

  .snackbar-stack-move {
    transition: transform 0.3s ease;
  }

  @media (max-width: 600px) {
    .stacking-snackbar-container {
      left: 16px;
      right: 16px;
      bottom: 16px;
    }

    .snackbar-item {
      min-width: auto;
      max-width: 100%;
    }
  }
</style>
